import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import type { WhatsAppAccountView, WhatsAppLinkResponse } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';
import { ChatList, GroupPicker, MediaSelect, StorageSelect } from './TelegramAccounts';

const QUERY_KEY = ['whatsapp-accounts'];
const POLL_MS = 2_000;

/** The QR code of a link in progress, refreshed by polling until WhatsApp confirms it. */
function LinkProgress({
  start,
  onDone,
  onCancel,
}: {
  start: WhatsAppLinkResponse;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState(start);
  const login = step.step === 'done' ? undefined : step.login;
  const finished = step.step === 'done' || step.step === 'failed';

  useEffect(() => {
    if (!login || finished) return;
    let stopped = false;
    const timer = setInterval(() => {
      api
        .whatsappLinkStatus(login)
        .then((next) => {
          if (!stopped) setStep(next);
        })
        .catch((err: unknown) => {
          if (stopped) return;
          setStep({
            step: 'failed',
            login,
            message: err instanceof Error ? err.message : String(err),
          });
        });
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [login, finished]);

  const cancel = () => {
    if (login && !finished) void api.whatsappLinkCancel(login).catch(() => undefined);
    onCancel();
  };

  if (step.step === 'done') {
    return (
      <div className="card form">
        <h3>Готово</h3>
        <p>
          Аккаунт <strong>{step.account.label}</strong>
          {step.account.phone && ` (${step.account.phone})`} привязан.
        </p>
        <div className="actions-row">
          <button type="button" className="btn btn--primary" onClick={onDone}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }
  if (step.step === 'failed') {
    return (
      <div className="card form">
        <h3>Не получилось</h3>
        <p className="text-error">{step.message}</p>
        <div className="actions-row">
          <button type="button" className="btn" onClick={onCancel}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="card form">
      <h3>Отсканируйте код в WhatsApp</h3>
      <p className="muted small">
        На телефоне: WhatsApp → Настройки (или меню ⋮) → Связанные устройства → Привязать
        устройство. Код меняется каждые 20–60 секунд; страница обновляет его сама.
      </p>
      {step.step === 'qr' ? (
        <div className="wa-qr">
          <img src={step.qr} alt="QR-код для привязки WhatsApp" width={288} height={288} />
          <span className="muted small">выдан {formatShortDateTime(step.qrAt)}</span>
        </div>
      ) : (
        <p className="muted">Соединяюсь с WhatsApp…</p>
      )}
      <div className="actions-row">
        <button type="button" className="btn btn--ghost" onClick={cancel}>
          Отмена
        </button>
      </div>
    </div>
  );
}

/** Key and label → the QR code. */
function LinkWizard({
  account,
  label: initialLabel,
  onClose,
}: {
  /** Linking an existing account again: its id is fixed. */
  account?: string;
  label?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [id, setId] = useState(account ?? '');
  const [label, setLabel] = useState(initialLabel ?? '');
  const [started, setStarted] = useState<WhatsAppLinkResponse | undefined>();
  const start = useMutation({
    mutationFn: () =>
      api.whatsappLinkStart({
        account: id.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
      }),
    onSuccess: setStarted,
  });
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    onClose();
  };

  if (started) return <LinkProgress start={started} onDone={done} onCancel={onClose} />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate();
  };
  return (
    <form className="card form" onSubmit={submit}>
      <h3>{account ? `Привязать заново: ${initialLabel ?? account}` : 'Новый аккаунт WhatsApp'}</h3>
      <p className="muted small">
        Агент становится «связанным устройством» обычного аккаунта WhatsApp, как WhatsApp Web:
        телефон остаётся главным. Устройство, которое не выходило на связь около двух недель,
        WhatsApp отвязывает сам. Сохраняются только ключи сессии (зашифрованными).
      </p>
      {!account && (
        <label>
          Ключ <span className="muted small">(латиница, например main; не меняется)</span>
          <input
            value={id}
            onChange={(e) => setId(e.target.value.toLowerCase())}
            pattern="[a-z][a-z0-9_\-]{1,31}"
            required
          />
        </label>
      )}
      <label>
        Название
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={id} />
      </label>
      {start.error && <p className="text-error small">{start.error.message}</p>}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={start.isPending}>
          {start.isPending ? 'Получаю код…' : 'Показать QR-код'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Отмена
        </button>
      </div>
    </form>
  );
}

function AccountCard({
  account,
  archiveHere,
  onLink,
}: {
  account: WhatsAppAccountView;
  archiveHere: boolean;
  onLink: () => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.whatsappAccountUpdate>[1]) =>
      api.whatsappAccountUpdate(account.id, patch),
    onSettled: refresh,
  });
  const unlink = useMutation({
    mutationFn: () => api.whatsappAccountUnlink(account.id),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.whatsappAccountRemove(account.id),
    onSettled: refresh,
  });

  const pill = account.lastError
    ? { cls: 'pill--error', text: 'ошибка' }
    : account.linked
      ? { cls: 'pill--ok', text: 'привязан' }
      : { cls: 'pill--warn', text: 'не привязан' };
  const error = update.error ?? unlink.error ?? remove.error;

  return (
    <div className="card">
      <div className="card__header">
        <h3>
          {account.label} <span className="muted small">· {account.id}</span>
        </h3>
        <span className={`pill ${pill.cls}`}>{pill.text}</span>
      </div>
      <dl className="facts">
        <dt>Аккаунт</dt>
        <dd>
          {account.displayName ?? '—'}
          {account.phone && <span className="muted"> · {account.phone}</span>}
        </dd>
        <dt>Контакты</dt>
        <dd>{account.contacts}</dd>
        <dt>Архив</dt>
        <dd>
          {account.chats} чатов, {account.messages} сообщений
          {account.archiving
            ? ' · идёт'
            : account.archive && account.linked && !archiveHere
              ? ' · на этом сервере архив выключен'
              : ''}
        </dd>
        {account.linkedAt && (
          <>
            <dt>Привязан</dt>
            <dd>{formatShortDateTime(account.linkedAt)}</dd>
          </>
        )}
        {account.lastError && (
          <>
            <dt>Ошибка</dt>
            <dd className="text-error">{account.lastError}</dd>
          </>
        )}
      </dl>

      <div className="toggles">
        <label className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={account.archive}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ archive: e.target.checked })}
          />
          Архивировать группы
        </label>
        <label className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={account.send}
            disabled={update.isPending}
            onChange={(e) => {
              if (
                !e.target.checked ||
                window.confirm(
                  'Разрешить агенту отправлять сообщения от этого аккаунта контактам? ' +
                    'Каждая отправка всё равно требует подтверждения.',
                )
              ) {
                update.mutate({ send: e.target.checked });
              }
            }}
          />
          Разрешить отправку сообщений
        </label>
      </div>

      <label className="small media-default">
        Файлы новых чатов
        <MediaSelect
          value={account.mediaDefault}
          disabled={update.isPending}
          onChange={(mediaDefault) => update.mutate({ mediaDefault })}
        />
      </label>
      <label className="small media-default">
        Хранилище новых чатов <span className="muted">(диск сервера или S3)</span>
        <StorageSelect
          value={account.storageDefault}
          disabled={update.isPending}
          onChange={(storageDefault) => update.mutate({ storageDefault })}
        />
      </label>

      {account.chats > 0 && <ChatList account={account.id} provider="whatsapp" />}
      {account.linked && (
        <GroupPicker
          account={account.id}
          provider="whatsapp"
          defaultMedia={account.mediaDefault}
          defaultStorage={account.storageDefault}
        />
      )}

      {error && <p className="text-error small">{error.message}</p>}
      <div className="actions-row">
        <button type="button" className="btn" onClick={onLink}>
          {account.linked ? 'Привязать заново' : 'Привязать'}
        </button>
        {account.linked && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={unlink.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Отвязать устройство? Оно исчезнет из связанных устройств WhatsApp, контакты останутся.',
                )
              ) {
                unlink.mutate();
              }
            }}
          >
            Отвязать
          </button>
        )}
        {account.contacts === 0 && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить аккаунт ${account.label}?`)) remove.mutate();
            }}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}

/** Settings → WhatsApp (owner only): the accounts the agent sends from. */
export function WhatsAppSection() {
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: api.whatsappAccounts });
  const [wizard, setWizard] = useState<{ account?: string; label?: string } | undefined>();
  const data = query.data;

  return (
    <section>
      <h2>WhatsApp</h2>
      <p className="muted">
        Аккаунты WhatsApp, от которых агент может писать контактам (
        <code>pnpm contacts add … --provider whatsapp</code>). Каждый — связанное устройство
        обычного аккаунта, привязанное по QR-коду.
      </p>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {data && !data.canStoreSecrets && (
        <p className="notice notice--error">
          На сервере не задан <code>SECRETS_KEY</code>: ключи сессии нельзя сохранить. Добавьте его
          в <code>.env</code> (<code>openssl rand -hex 32</code>) и перезапустите сервер.
        </p>
      )}
      {wizard ? (
        <LinkWizard
          {...(wizard.account ? { account: wizard.account } : {})}
          {...(wizard.label ? { label: wizard.label } : {})}
          onClose={() => setWizard(undefined)}
        />
      ) : (
        data?.canStoreSecrets && (
          <div className="actions-row">
            <button type="button" className="btn btn--primary" onClick={() => setWizard({})}>
              Добавить аккаунт
            </button>
          </div>
        )
      )}
      {data && data.accounts.length === 0 && <p className="muted">Аккаунтов пока нет.</p>}
      <div className="stack">
        {data?.accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            archiveHere={data.archiveHere}
            onLink={() => setWizard({ account: account.id, label: account.label })}
          />
        ))}
      </div>
    </section>
  );
}
